import { sql } from "drizzle-orm";
import {
  foreignKey,
  index,
  integer,
  real,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import { activityInstance } from "@/db/schema/activity-instance";
import { mvActivity } from "@/db/schema/movement";
import type { MediaAssetSlim } from "@/lib/media";
import type { MusicAttachment } from "@/lib/music/attachment";
import type { CommentMedia } from "@/validations/comment";
import {
  type ActivityCompetitionMode,
  type ActivitySessionFormat,
  activity,
} from "./activities";
import { user } from "./auth";
import { chat } from "./chat";
import { venueBooking } from "./venue-booking";
import { verificationCase } from "./verification-case";

// ──────────────────────────────────────────────────────────────────────────────
// VISIBILITY & STATUS ENUMS
// ──────────────────────────────────────────────────────────────────────────────

export const ACTIVITY_SESSION_REMINDER_OFFSET_MINUTES = 30;
export const ACTIVITY_SESSION_RESULT_CONFIRM_REMINDER_OFFSETS_HOURS = [
  6, 24,
] as const;

export const ACTIVITY_SESSION_VISIBILITY = [
  "public", // Open to anyone on the app
  "buddies", // Visible only to user's connections
  "close_friends", // Visible only to the creator's close-friends list
  "private", // Invite-only, not shown in feed
] as const;
export type ActivitySessionVisibility =
  (typeof ACTIVITY_SESSION_VISIBILITY)[number];

export const ACTIVITY_SESSION_STATUS = [
  "upcoming", // Scheduled, accepting participants
  "in_progress", // Game is happening now
  "completed", // Game finished, awaiting/has scores
  "cancelled", // Game was cancelled
] as const;
export type ActivitySessionStatus = (typeof ACTIVITY_SESSION_STATUS)[number];

export const ACTIVITY_SESSION_CANCEL_REASON = [
  "insufficient_players",
  "creator_cancelled",
  "admin_cancelled",
  "other",
] as const;
export type ActivitySessionCancelReason =
  (typeof ACTIVITY_SESSION_CANCEL_REASON)[number];

// Pricing: free vs paid (payments may be handled later)
export const ACTIVITY_SESSION_PRICING_TYPE = ["free", "paid"] as const;
export type ActivitySessionPricingType =
  (typeof ACTIVITY_SESSION_PRICING_TYPE)[number];

export const ACTIVITY_SESSION_ENTRY_TYPE = ["session", "retro_result"] as const;
export type ActivitySessionEntryType =
  (typeof ACTIVITY_SESSION_ENTRY_TYPE)[number];

// Game type: casual vs ranked
export const ACTIVITY_COMPETITION_MODES = ["casual", "competitive"] as const;
export type ActivitySessionCompetitionMode = ActivityCompetitionMode;

// Game format based on activity type
export const ACTIVITY_SESSION_FORMATS = [
  "singles", // 1v1 (tennis, badminton, squash, table tennis)
  "doubles", // 2v2 (tennis, badminton, padel)
  "team", // Multi-player team (football, cricket, basketball, volleyball)
  "group", // Solo/group activities (gym, yoga, running, swimming)
] as const;
export type { ActivityCompetitionMode, ActivitySessionFormat };

// ──────────────────────────────────────────────────────────────────────────────
// GAME EVENT (main table)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Stores user-created game/sporting events (pickup games, matches, etc.)
 * Uses the same activity templates as activity posts for consistency.
 */
export const activitySession = sqliteTable(
  "activity_session",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    creatorId: text("creator_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    activityId: text("activity_id")
      .notNull()
      .references(() => activity.id, { onDelete: "restrict" }),

    // Basic info
    title: text("title").notNull(),
    description: text("description").notNull(),

    // Separates scheduled joinable sessions from score-only entries created
    // after the match already happened.
    entryType: text("entry_type")
      .notNull()
      .$type<ActivitySessionEntryType>()
      .default("session"),

    // Visibility level
    visibility: text("visibility")
      .notNull()
      .$type<ActivitySessionVisibility>()
      .default("public"),

    // When enabled, non-invited users must request approval before joining.
    requiresApproval: integer("requires_approval", { mode: "boolean" })
      .notNull()
      .default(false),

    status: text("status")
      .notNull()
      .$type<ActivitySessionStatus>()
      .default("upcoming"),

    cancelReason: text("cancel_reason").$type<ActivitySessionCancelReason>(),

    // Casual or competitive session.
    competitionMode: text("competition_mode")
      .$type<ActivityCompetitionMode>()
      .default("casual"),

    // Session format: singles, doubles, team, or group.
    sessionFormat: text("session_format").$type<ActivitySessionFormat>(),

    // Scheduling
    startTime: integer("start_time", { mode: "timestamp" }).notNull(),
    endTime: integer("end_time", { mode: "timestamp" }).notNull(),

    // Location
    locationName: text("location_name").notNull(),
    address: text("address").notNull(),
    latitude: real("latitude").notNull(),
    longitude: real("longitude").notNull(),

    // Pricing
    pricingType: text("pricing_type")
      .notNull()
      .$type<ActivitySessionPricingType>()
      .default("free"),
    pricePerPersonAmountMinor: integer("price_per_person_amount_minor")
      .notNull()
      .default(0),
    currency: text("currency").notNull().default("INR"),

    // Optional skill gate for score-capable sessions
    minSkillLevel: integer("min_skill_level"),
    maxSkillLevel: integer("max_skill_level"),

    // Participant limits
    minParticipants: integer("min_participants").notNull().default(2),
    maxParticipants: integer("max_participants").notNull().default(10),

    // Auto-created group chat for coordination
    chatId: integer("chat_id").references(() => chat.id, {
      onDelete: "set null",
    }),

    // A venue booking the organizer paid for themselves. This is a TAG and
    // nothing more: it drives a "Court confirmed" badge so people trust the
    // session is real. The split never reads its price, never reconciles
    // against it, and Hiive never needs to know what the venue cost. Usually
    // set after creation — people agree a time first, then someone books.
    venueBookingId: integer("venue_booking_id").references(
      () => venueBooking.id,
      { onDelete: "set null" },
    ),

    // Unique invite code for external sharing (e.g., "ABC123")
    inviteCode: text("invite_code").unique(),

    ...timestamps,
  },
  (t) => [
    index("activity_session_creator_idx").on(t.creatorId),
    index("activity_session_activity_idx").on(t.activityId),
    index("activity_session_visibility_idx").on(t.visibility),
    index("activity_session_status_idx").on(t.status),
    index("activity_session_start_time_idx").on(t.startTime),
    index("activity_session_location_idx").on(t.latitude, t.longitude),
    index("activity_session_venue_booking_idx").on(t.venueBookingId),
  ],
);

export type ActivitySession = typeof activitySession.$inferSelect;
export type ActivitySessionInsert = typeof activitySession.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// GAME EVENT PARTICIPANT
// ──────────────────────────────────────────────────────────────────────────────

export const PARTICIPANT_ROLE = ["creator", "participant"] as const;
export type GameParticipantRole = (typeof PARTICIPANT_ROLE)[number];

export const PARTICIPANT_STATUS = [
  "confirmed", // Actively participating
  "pending", // Awaiting confirmation (for invite-based)
  "kicked", // Removed by creator
  "left", // Voluntarily left
] as const;
export type GameParticipantStatus = (typeof PARTICIPANT_STATUS)[number];

/**
 * Tracks who has joined a activity session.
 */
export const activitySessionParticipant = sqliteTable(
  "activity_session_participant",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    activitySessionId: integer("activity_session_id")
      .notNull()
      .references(() => activitySession.id, { onDelete: "cascade" }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    role: text("role")
      .notNull()
      .$type<GameParticipantRole>()
      .default("participant"),

    status: text("status")
      .notNull()
      .$type<GameParticipantStatus>()
      .default("confirmed"),

    // Team assignment for team sports (optional)
    // Uses neutral naming: "team1" | "team2"
    team: text("team"),

    joinedAt: integer("joined_at", { mode: "timestamp" })
      .notNull()
      .$defaultFn(() => new Date()),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("activity_session_participant_unique").on(
      t.activitySessionId,
      t.userId,
    ),
    index("activity_session_participant_session_idx").on(t.activitySessionId),
    index("activity_session_participant_user_idx").on(t.userId),
    index("activity_session_participant_status_idx").on(t.status),
    // "Sessions joined" (stickers, card rules) filters user AND status; with
    // only single-column indexes SQLite picks `status` and scans every
    // confirmed participant in the app.
    index("activity_session_participant_user_status_idx").on(
      t.userId,
      t.status,
    ),
  ],
);

export type ActivitySessionParticipant =
  typeof activitySessionParticipant.$inferSelect;
export type ActivitySessionParticipantInsert =
  typeof activitySessionParticipant.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// GAME EVENT JOIN REQUEST
// ──────────────────────────────────────────────────────────────────────────────

export const ACTIVITY_SESSION_JOIN_REQUEST_STATUS = [
  "pending",
  "approved",
  "denied",
  "canceled",
  "expired",
] as const;
export type ActivitySessionJoinRequestStatus =
  (typeof ACTIVITY_SESSION_JOIN_REQUEST_STATUS)[number];

/**
 * Request queue for games that require organizer approval.
 */
export const activitySessionJoinRequest = sqliteTable(
  "activity_session_join_request",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    activitySessionId: integer("activity_session_id")
      .notNull()
      .references(() => activitySession.id, { onDelete: "cascade" }),

    requesterUserId: text("requester_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    reviewerUserId: text("reviewer_user_id").references(() => user.id, {
      onDelete: "set null",
    }),

    status: text("status")
      .notNull()
      .$type<ActivitySessionJoinRequestStatus>()
      .default("pending"),

    requestedAt: integer("requested_at", { mode: "timestamp" }).notNull(),
    reviewedAt: integer("reviewed_at", { mode: "timestamp" }),
    reviewNote: text("review_note"),
    preferredTeam: text("preferred_team"),

    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("activity_session_join_request_unique_pending")
      .on(t.activitySessionId, t.requesterUserId)
      .where(sql`${t.status} = 'pending'`),
    index("activity_session_join_request_requester_status_requested_idx").on(
      t.requesterUserId,
      t.status,
      t.requestedAt,
      t.id,
    ),
    index("activity_session_join_request_session_status_requested_idx").on(
      t.activitySessionId,
      t.status,
      t.requestedAt,
      t.id,
    ),
    index("activity_session_join_request_status_requested_idx").on(
      t.status,
      t.requestedAt,
      t.id,
    ),
  ],
);

export type ActivitySessionJoinRequest =
  typeof activitySessionJoinRequest.$inferSelect;
export type ActivitySessionJoinRequestInsert =
  typeof activitySessionJoinRequest.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// GAME EVENT INVITE
// ──────────────────────────────────────────────────────────────────────────────

export const INVITE_STATUS = [
  "pending",
  "accepted",
  "declined",
  "expired",
] as const;
export type ActivitySessionInviteStatus = (typeof INVITE_STATUS)[number];

/**
 * Tracks invitations to activity sessions.
 * Supports both internal (user-to-user) and external (shareable link) invites.
 */
export const activitySessionInvite = sqliteTable(
  "activity_session_invite",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    activitySessionId: integer("activity_session_id")
      .notNull()
      .references(() => activitySession.id, { onDelete: "cascade" }),

    invitedByUserId: text("invited_by_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    // If null, this is an external invite (link-based)
    invitedUserId: text("invited_user_id").references(() => user.id, {
      onDelete: "cascade",
    }),

    status: text("status")
      .notNull()
      .$type<ActivitySessionInviteStatus>()
      .default("pending"),

    expiresAt: integer("expires_at", { mode: "timestamp" }),
    seenAt: integer("seen_at", { mode: "timestamp" }),

    ...timestamps,
  },
  (t) => [
    index("activity_session_invite_session_idx").on(t.activitySessionId),
    index("activity_session_invite_invited_user_idx").on(t.invitedUserId),
    index("activity_session_invite_status_idx").on(t.status),
    // One pending invite per user per game
    uniqueIndex("activity_session_invite_unique_pending").on(
      t.activitySessionId,
      t.invitedUserId,
      t.status,
    ),
  ],
);

export type ActivitySessionInvite = typeof activitySessionInvite.$inferSelect;
export type ActivitySessionInviteInsert =
  typeof activitySessionInvite.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// GAME EVENT SCORE
// ──────────────────────────────────────────────────────────────────────────────

export const SCORE_VERIFICATION_STATUS = [
  "pending", // Awaiting opponent verification
  "verified", // Opponent confirmed the score
  "disputed", // Opponent disputes the score
  "expired", // Not confirmed in time; discarded
] as const;
export type ScoreVerificationStatus =
  (typeof SCORE_VERIFICATION_STATUS)[number];
export type ActivitySessionResultVerificationStatus = ScoreVerificationStatus;

/**
 * Stores post-game scores with verification.
 * Uses the same data structure as activity post templates for consistency.
 */
export const activitySessionResult = sqliteTable(
  "activity_session_result",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    activitySessionId: integer("activity_session_id")
      .notNull()
      .references(() => activitySession.id, { onDelete: "cascade" }),

    // Optional link to the shared verification system (Phase 1: schema only).
    verificationCaseId: integer("verification_case_id").references(
      () => verificationCase.id,
      { onDelete: "set null" },
    ),

    submittedByUserId: text("submitted_by_user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    // Flexible JSON data matching activity post template structure
    // Example for tennis: { mode: "singles", scores: ["6-4", "7-5"], winner: "your-team" }
    scoreData: text("score_data", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    ...timestamps,
  },
  (t) => [
    index("activity_session_result_session_idx").on(t.activitySessionId),
    index("activity_session_result_submitter_idx").on(t.submittedByUserId),
    // Only one score submission per game (for competitive events)
    uniqueIndex("activity_session_result_unique").on(t.activitySessionId),
    uniqueIndex("activity_session_result_verification_case_unique").on(
      t.verificationCaseId,
    ),
  ],
);

export type ActivitySessionResult = typeof activitySessionResult.$inferSelect;
export type ActivitySessionResultInsert =
  typeof activitySessionResult.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// GAME EVENT SCORE RATING CHANGE (idempotent rating ledger)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Rating/stat deltas applied when a competitive score revision becomes verified.
 * Used for idempotency and future reversals/auditing.
 */
export const activitySessionResultRatingChange = sqliteTable(
  "activity_session_result_rating_change",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    activitySessionId: integer("activity_session_id")
      .notNull()
      .references(() => activitySession.id, { onDelete: "cascade" }),

    scoreId: integer("score_id")
      .notNull()
      .references(() => activitySessionResult.id, { onDelete: "cascade" }),

    revision: integer("revision").notNull(),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    activityId: text("activity_id")
      .notNull()
      .references(() => activity.id, { onDelete: "cascade" }),

    gamesPlayedDelta: integer("games_played_delta").notNull(),
    winsDelta: integer("wins_delta").notNull(),
    lossesDelta: integer("losses_delta").notNull(),
    drawsDelta: integer("draws_delta").notNull(),

    ratingBefore: integer("rating_before").notNull(),
    ratingDelta: integer("rating_delta").notNull(),
    ratingAfter: integer("rating_after").notNull(),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("activity_session_result_rating_change_unique").on(
      t.scoreId,
      t.revision,
      t.userId,
    ),
    index("activity_session_result_rating_change_event_idx").on(
      t.activitySessionId,
    ),
    index("activity_session_result_rating_change_score_idx").on(
      t.scoreId,
      t.revision,
    ),
    index("activity_session_result_rating_change_user_idx").on(t.userId),
    index("activity_session_result_rating_change_activity_idx").on(
      t.activityId,
    ),
  ],
);

export type ActivitySessionResultRatingChange =
  typeof activitySessionResultRatingChange.$inferSelect;
export type ActivitySessionResultRatingChangeInsert =
  typeof activitySessionResultRatingChange.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// GAME EVENT PARTICIPANT SCORE (individual scores for non-competitive events)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * For non-competitive activities (yoga, gym, hyrox, trekking, etc.)
 * Each participant logs their OWN individual performance.
 * No verification needed - it's personal data.
 */
export const activitySessionParticipantResult = sqliteTable(
  "activity_session_participant_result",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    activitySessionId: integer("activity_session_id")
      .notNull()
      .references(() => activitySession.id, { onDelete: "cascade" }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    // Individual performance data (activity-specific)
    // Examples:
    // - Yoga: { duration: 60, poses: ["warrior", "downdog"], intensity: "moderate" }
    // - Gym: { exercises: [...], totalWeight: 5000, duration: 75 }
    // - Hyrox: { time: "1:12:30", stations: { SkiErg: "3:45", Sled: "2:30" } }
    // - Running: { distance: 10, time: "55:30", pace: "5:33/km" }
    performanceData: text("performance_data", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    // Optional link to an external provider activity instance (Movement, Strava, etc.)
    activityInstanceId: integer("activity_instance_id").references(
      () => activityInstance.id,
      { onDelete: "set null" },
    ),

    // Optional notes from the participant
    notes: text("notes"),

    ...timestamps,
  },
  (t) => [
    // Each participant can only submit one score per event
    uniqueIndex("activity_session_participant_result_unique").on(
      t.activitySessionId,
      t.userId,
    ),
    index("activity_session_participant_result_event_idx").on(
      t.activitySessionId,
    ),
    index("activity_session_participant_result_user_idx").on(t.userId),
    index("activity_session_participant_result_instance_idx").on(
      t.activityInstanceId,
    ),
  ],
);

export type ActivitySessionParticipantResult =
  typeof activitySessionParticipantResult.$inferSelect;
export type ActivitySessionParticipantResultInsert =
  typeof activitySessionParticipantResult.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// GAME EVENT PARTICIPANT SCORE MOVEMENT (Movement integration)
// ──────────────────────────────────────────────────────────────────────────────

export const activitySessionParticipantResultMovement = sqliteTable(
  "activity_session_participant_result_movement",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    participantScoreId: integer("participant_score_id")
      .notNull()
      .references(() => activitySessionParticipantResult.id, {
        onDelete: "cascade",
      }),
    mvActivityId: text("mv_activity_id")
      .notNull()
      .references(() => mvActivity.id, { onDelete: "cascade" }),
    startedAt: integer("started_at", { mode: "timestamp" }),
    durationSeconds: integer("duration_seconds"),
    distanceMeters: real("distance_meters"),
    avgPaceSecondsPerKm: real("avg_pace_seconds_per_km"),
    ...timestamps,
  },
  (t) => [
    uniqueIndex("activity_session_participant_result_movement_unique").on(
      t.participantScoreId,
    ),
    uniqueIndex("activity_session_participant_result_movement_mv_unique").on(
      t.mvActivityId,
    ),
    index("activity_session_participant_result_movement_by_score").on(
      t.participantScoreId,
    ),
  ],
);

export type ActivitySessionParticipantResultMovement =
  typeof activitySessionParticipantResultMovement.$inferSelect;
export type ActivitySessionParticipantResultMovementInsert =
  typeof activitySessionParticipantResultMovement.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// USER GAME SCORE (for competitive activities: tennis, basketball, etc.)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Tracks win/loss record for competitive activities.
 * Updated when a activity session result is verified.
 */
export const userActivitySessionResult = sqliteTable(
  "user_activity_session_result",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    activityId: text("activity_id")
      .notNull()
      .references(() => activity.id, { onDelete: "cascade" }),

    // Competitive stats
    gamesPlayed: integer("games_played").notNull().default(0),
    wins: integer("wins").notNull().default(0),
    losses: integer("losses").notNull().default(0),
    draws: integer("draws").notNull().default(0),

    // Computed rating (ELO-style or simple win rate)
    rating: integer("rating").notNull().default(1000),

    lastPlayedAt: integer("last_played_at", { mode: "timestamp" }),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("user_activity_session_result_unique").on(
      t.userId,
      t.activityId,
    ),
    index("user_activity_session_result_user_idx").on(t.userId),
    index("user_activity_session_result_activity_idx").on(t.activityId),
    index("user_activity_session_result_rating_idx").on(t.rating),
  ],
);

export type UserActivitySessionResult =
  typeof userActivitySessionResult.$inferSelect;
export type UserActivitySessionResultInsert =
  typeof userActivitySessionResult.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// USER ACTIVITY STATS (for non-competitive: yoga, hyrox, gym, trekking, etc.)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Tracks performance metrics for non-competitive activities.
 * No winners/losers - just personal progress tracking.
 */
export const userActivityStats = sqliteTable(
  "user_activity_stats",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    activityId: text("activity_id")
      .notNull()
      .references(() => activity.id, { onDelete: "cascade" }),

    // General stats
    sessionsCompleted: integer("sessions_completed").notNull().default(0),
    totalDurationMins: integer("total_duration_mins").notNull().default(0),

    // Flexible metrics per activity type (stored as JSON)
    // Examples:
    // - Hyrox: { bestTime: "1:08:30", avgTime: "1:15:00" }
    // - Running: { totalDistanceKm: 250, bestPaceMinPerKm: 5.5 }
    // - Gym: { totalWeightLifted: 50000, favoriteLift: "deadlift" }
    // - Yoga: { totalMinutes: 6000, streakDays: 30 }
    performanceMetrics: text("performance_metrics", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    lastSessionAt: integer("last_session_at", { mode: "timestamp" }),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("user_activity_stats_unique").on(t.userId, t.activityId),
    index("user_activity_stats_user_idx").on(t.userId),
    index("user_activity_stats_activity_idx").on(t.activityId),
  ],
);

export type UserActivityStats = typeof userActivityStats.$inferSelect;
export type UserActivityStatsInsert = typeof userActivityStats.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// GAME EVENT LIKE
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Tracks likes on activity sessions.
 */
export const activitySessionLike = sqliteTable(
  "activity_session_like",
  {
    activitySessionId: integer("activity_session_id")
      .notNull()
      .references(() => activitySession.id, { onDelete: "cascade" }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("activity_session_like_unique").on(
      t.activitySessionId,
      t.userId,
    ),
    index("activity_session_like_event_idx").on(t.activitySessionId),
    index("activity_session_like_user_idx").on(t.userId),
  ],
);

export type ActivitySessionLike = typeof activitySessionLike.$inferSelect;
export type ActivitySessionLikeInsert = typeof activitySessionLike.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// GAME EVENT COMMENT
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Stores comments on activity sessions with recursive nested replies.
 * parentCommentId: NULL => top-level comment on the activity session
 * parentCommentId: non-NULL => direct parent at any depth
 */
export const activitySessionComment = sqliteTable(
  "activity_session_comment",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    activitySessionId: integer("activity_session_id")
      .notNull()
      .references(() => activitySession.id, { onDelete: "cascade" }),

    authorId: text("author_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    parentCommentId: integer("parent_comment_id"),
    rootCommentId: integer("root_comment_id"),
    depth: integer("depth").notNull().default(0),

    assistantName: text("assistant_name"),
    assistantHandle: text("assistant_handle"),
    content: text("content").notNull(),
    voiceAsset: text("voice_asset", {
      mode: "json",
    }).$type<MediaAssetSlim | null>(),
    voiceDurationMs: integer("voice_duration_ms"),
    voiceWaveform: text("voice_waveform", { mode: "json" }).$type<
      number[] | null
    >(),
    media: text("media", { mode: "json" }).$type<CommentMedia | null>(),
    /** A song reply (catalog snapshot, server-resolved); null = none. */
    music: text("music", { mode: "json" }).$type<MusicAttachment | null>(),
    isPinned: integer("is_pinned", { mode: "boolean" })
      .notNull()
      .default(false),
    pinnedAt: integer("pinned_at", { mode: "timestamp" }),
    pinnedById: text("pinned_by_id").references(() => user.id, {
      onDelete: "set null",
    }),

    ...timestamps,
  },
  (t) => [
    index("activity_session_comment_event_idx").on(t.activitySessionId),
    index("activity_session_comment_author_idx").on(t.authorId),
    index("activity_session_comment_parent_idx").on(t.parentCommentId),
    index("activity_session_comment_root_idx").on(t.rootCommentId),
    index("activity_session_comment_pinned_idx").on(
      t.activitySessionId,
      t.isPinned,
    ),
    foreignKey({
      name: "activity_session_comment_parent_fk",
      columns: [t.parentCommentId],
      foreignColumns: [t.id],
    }).onDelete("cascade"),
  ],
);

export type ActivitySessionComment = typeof activitySessionComment.$inferSelect;
export type ActivitySessionCommentInsert =
  typeof activitySessionComment.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// GAME EVENT COMMENT LIKE
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Tracks likes on activity session comments.
 */
export const activitySessionCommentLike = sqliteTable(
  "activity_session_comment_like",
  {
    commentId: integer("comment_id")
      .notNull()
      .references(() => activitySessionComment.id, { onDelete: "cascade" }),

    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("activity_session_comment_like_unique").on(
      t.commentId,
      t.userId,
    ),
    index("activity_session_comment_like_comment_idx").on(t.commentId),
    index("activity_session_comment_like_user_idx").on(t.userId),
  ],
);

export type ActivitySessionCommentLike =
  typeof activitySessionCommentLike.$inferSelect;
export type ActivitySessionCommentLikeInsert =
  typeof activitySessionCommentLike.$inferInsert;

// ──────────────────────────────────────────────────────────────────────────────
// GAME EVENT TIMELINE (Audit Trail)
// ──────────────────────────────────────────────────────────────────────────────

export const activitySessionTimeline = sqliteTable(
  "activity_session_timeline",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),

    activitySessionId: integer("activity_session_id")
      .notNull()
      .references(() => activitySession.id, { onDelete: "cascade" }),

    // "created", "joined", "left", "score_submitted", "score_verified", "score_disputed", "game_cancelled", "game_completed"
    type: text("type").notNull(),

    // Human readable content summary
    content: text("content").notNull(),

    // JSON payload for structured data (e.g. key diffs, reason)
    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    actorId: text("actor_id").references(() => user.id, {
      onDelete: "set null",
    }),

    ...timestamps,
  },
  (t) => [
    index("activity_session_timeline_event_idx").on(t.activitySessionId),
    index("activity_session_timeline_created_idx").on(t.createdAt),
  ],
);

export type ActivitySessionTimeline =
  typeof activitySessionTimeline.$inferSelect;
export type ActivitySessionTimelineInsert =
  typeof activitySessionTimeline.$inferInsert;
